import * as React from 'react'
import { create } from '@bufbuild/protobuf'
import { toast } from 'sonner'

import { CreateAttachmentRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import type { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { useCreateAttachment } from '@/stores/attachments.store'
import { readFileAsBase64 } from '@/lib/file-utils'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Label } from '@/components/ui/label'
import {
  Dropzone,
  DropzoneContent,
  DropzoneEmptyState,
} from '@/components/ui/shadcn-io/dropzone'
import { Paperclip } from 'lucide-react'

interface AttachmentSheetProps {
  organizationId: string
  parentType: ParentTypeEnum
  parentId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

const ACCEPT_MAP = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'application/pdf': ['.pdf'],
}
const MAX_SIZE_MB = 23
const MAX_SIZE = MAX_SIZE_MB * 1024 * 1024


export function AttachmentSheet({
  organizationId,
  parentType,
  parentId,
  open,
  onOpenChange,
}: AttachmentSheetProps) {
  const { mutation } = useCreateAttachment()
  const [file, setFile] = React.useState<File | undefined>(undefined)

  // Reset file when sheet closes
  React.useEffect(() => {
    if (!open) {
      setFile(undefined)
    }
  }, [open])

  const handleDrop = (files: File[]) => {
    if (files.length === 0) return
    setFile(files[0])
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!file) {
      toast.error('Please select a file to upload')
      return
    }

    try {
      const fileBase64 = await readFileAsBase64(file)
      const request = create(CreateAttachmentRequestSchema, {
        organizationId,
        parentType,
        parentId,
        filename: file.name,
        contentType: file.type,
        fileBase64,
      })

      await mutation.mutateAsync(request)
      toast.success('Attachment uploaded successfully')
      setFile(undefined)
      onOpenChange(false)
    } catch (error) {
      console.error('Failed to upload attachment:', error)
      reportError(error, { feature: 'attachment_upload', kind: 'mutation' })
      toast.error('Failed to upload attachment', {
        description: getErrorDescription(error),
      })
    }
  }

  const isPending = mutation.isPending
  const isDisabled = isPending || !file

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Upload Attachment</SheetTitle>
          <SheetDescription>
            Upload a file (PNG, JPEG, or PDF, up to {MAX_SIZE_MB} MB).
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          <form
            id="attachment-form"
            onSubmit={handleSubmit}
            className="grid auto-rows-min gap-6"
          >
            <div className="grid">
              <Label className="mb-3">
                File <span className="text-destructive">*</span>
              </Label>
              <Dropzone
                accept={ACCEPT_MAP}
                maxFiles={1}
                maxSize={MAX_SIZE}
                onDrop={handleDrop}
                onError={(error) => toast.error(error.message)}
                disabled={isPending}
                src={file ? [file] : undefined}
              >
                <DropzoneEmptyState>
                  <div className="flex flex-col items-center justify-center">
                    <Paperclip className="size-8 text-muted-foreground" />
                    <p className="my-2 font-medium text-sm">Upload a file</p>
                    <p className="text-muted-foreground text-xs">
                      Drag and drop or click to upload
                    </p>
                    <p className="text-muted-foreground text-xs">
                      PNG, JPEG, or PDF · Max {MAX_SIZE_MB} MB
                    </p>
                  </div>
                </DropzoneEmptyState>
                <DropzoneContent />
              </Dropzone>
            </div>
          </form>
        </SheetBody>
        <SheetFooter>
          <SheetClose asChild>
            <Button type="button" variant="outline" disabled={isPending}>
              Cancel
            </Button>
          </SheetClose>
          <Button type="submit" form="attachment-form" disabled={isDisabled}>
            {isPending ? 'Uploading...' : 'Upload'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
